import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Brain, 
  Heart, 
  Users, 
  Sparkles, 
  ArrowRight,
  BookOpen,
  CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function IntroduccionPage() {
  const concepts = [
    {
      icon: Users,
      title: "No son personalidades múltiples",
      description: "Los arquetipos son facetas naturales de la psique humana, no trastornos. Todos tenemos múltiples 'voces' internas que surgieron para ayudarnos a sobrevivir y adaptarnos."
    },
    {
      icon: Heart,
      title: "Cada uno tiene un propósito",
      description: "Incluso los arquetipos que parecen 'negativos' como el Crítico Interior surgieron para protegerte. La clave no es eliminarlos, sino comprender su función."
    },
    {
      icon: Brain,
      title: "Bases en neurociencia",
      description: "Cada arquetipo activa patrones específicos en el cerebro: redes neuronales, neurotransmisores y estructuras particulares que explican sus manifestaciones."
    },
    {
      icon: Sparkles,
      title: "El camino es la integración",
      description: "El objetivo no es 'arreglar' tus partes, sino crear un diálogo armonioso entre ellas, liderado por tu Self (Yo Superior) — tu centro de consciencia compasiva."
    }
  ];

  const models = [
    {
      name: "Psicología Analítica",
      author: "Carl Gustav Jung",
      description: "Jung identificó que la psique humana contiene patrones universales (arquetipos) que se manifiestan en mitos, sueños y comportamientos a través de todas las culturas.",
      quote: "Hasta que lo inconsciente no se haga consciente, el subconsciente seguirá dirigiendo tu vida y tú lo llamarás destino."
    },
    {
      name: "Internal Family Systems (IFS)",
      author: "Richard Schwartz",
      description: "IFS propone que la mente es naturalmente múltiple, compuesta por 'partes' que pueden estar en armonía o conflicto. El Self es el líder natural que puede sanar y armonizar todas las partes.",
      quote: "No hay malas partes, solo partes en roles extremos que necesitan ser comprendidas y aliviadas."
    },
    {
      name: "Neurociencia Afectiva",
      author: "Jaak Panksepp y otros",
      description: "La investigación moderna confirma que diferentes estados emocionales activan circuitos cerebrales distintos, validando la experiencia de tener 'múltiples voces' internas.",
      quote: "Las emociones no son irracionales; son la base de nuestra inteligencia adaptativa."
    }
  ];

  const qualities8C = [
    { name: "Compasión", desc: "Amor incondicional hacia todas las partes" },
    { name: "Curiosidad", desc: "Interés genuino sin juicio" },
    { name: "Claridad", desc: "Perspectiva amplia y discernimiento" },
    { name: "Calma", desc: "Presencia serena ante las tormentas" },
    { name: "Confianza", desc: "Fe en el proceso y en ti mismo/a" },
    { name: "Coraje", desc: "Valentía para enfrentar lo difícil" },
    { name: "Creatividad", desc: "Capacidad de encontrar nuevas soluciones" },
    { name: "Conectividad", desc: "Sentido de unión con algo mayor" }
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-4xl sm:text-5xl text-white mb-6">
            ¿Qué son los{" "}
            <span className="text-secondary italic">Arquetipos Internos</span>?
          </h1>
          <p className="font-body text-lg text-primary-200 max-w-2xl mx-auto">
            Una introducción a tu ecosistema interior y las voces que te habitan
          </p>
        </motion.div>

        {/* Key Concepts */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {concepts.map((concept, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-primary/20 rounded-full p-3 shrink-0">
                    <concept.icon className="text-primary-300" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-white mb-2">
                      {concept.title}
                    </h3>
                    <p className="font-body text-sm text-primary-200 leading-relaxed">
                      {concept.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* The Self Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="glass-card rounded-2xl p-8 sm:p-10 border-l-4 border-secondary">
            <h2 className="font-heading text-3xl text-white mb-6">
              El Self: Tu Centro de Consciencia
            </h2>
            <p className="font-body text-primary-200 mb-6 leading-relaxed">
              En el modelo IFS, el <strong className="text-white">Self</strong> no es otro arquetipo más, 
              sino tu esencia no condicionada — quien realmente eres debajo de todas las partes protectoras y heridas. 
              El Self tiene cualidades naturales conocidas como las "8 C":
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
              {qualities8C.map((quality, index) => (
                <motion.div
                  key={quality.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-secondary/10 rounded-lg p-4 text-center"
                >
                  <p className="font-heading text-lg text-secondary mb-1">{quality.name}</p>
                  <p className="font-body text-xs text-primary-300">{quality.desc}</p>
                </motion.div>
              ))}
            </div>

            <p className="font-body text-primary-200 mt-8 leading-relaxed">
              Cuando actúas desde el Self, puedes sostener todas tus partes con compasión, 
              escucharlas sin fusionarte con ellas, y tomar decisiones integradas. 
              <strong className="text-white"> El objetivo del trabajo con arquetipos es aumentar tu acceso al Self.</strong>
            </p>
          </div>
        </motion.section>

        {/* Theoretical Models */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="font-heading text-3xl text-white text-center mb-10">
            Fundamentos Teóricos
          </h2>
          
          <div className="space-y-6">
            {models.map((model, index) => (
              <motion.div
                key={model.name}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="glass-card rounded-xl p-6 sm:p-8"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="bg-accent/20 rounded-full p-3 shrink-0 self-start">
                    <BookOpen className="text-accent-glow" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl text-white mb-1">
                      {model.name}
                    </h3>
                    <p className="font-body text-sm text-secondary mb-3">
                      {model.author}
                    </p>
                    <p className="font-body text-primary-200 mb-4 leading-relaxed">
                      {model.description}
                    </p>
                    <blockquote className="border-l-2 border-primary/50 pl-4 italic text-primary-300 text-sm">
                      "{model.quote}"
                    </blockquote>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* What to Expect */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="font-heading text-3xl text-white text-center mb-10">
            ¿Qué Esperar del Test?
          </h2>

          <div className="glass-card rounded-xl p-8">
            <div className="space-y-4">
              {[
                "30 preguntas divididas en 3 secciones (15-20 minutos)",
                "Identificación de voces, patrones relacionales y respuestas al estrés",
                "Identificación de tus 5-7 arquetipos más activos",
                "Mapa visual de tu ecosistema interior",
                "Análisis de conflictos y coaliciones entre tus partes",
                "Prácticas personalizadas de armonización",
                "Reporte completo descargable en PDF"
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="text-secondary shrink-0" size={20} />
                  <span className="font-body text-primary-100">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Disclaimer */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-primary-950/50 rounded-xl p-6 border border-primary/20">
            <h3 className="font-heading text-lg text-primary-200 mb-2">
              Nota Importante
            </h3>
            <p className="font-body text-sm text-primary-300 leading-relaxed">
              Esta herramienta es educativa y de autoconocimiento. No es un diagnóstico psicológico 
              ni reemplaza la terapia profesional. Si experimentas disociación, trauma complejo no resuelto, 
              o dificultades significativas, busca apoyo profesional con un terapeuta IFS certificado 
              o psicoterapeuta junguiano.
            </p>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link to="/test" data-testid="intro-cta-test">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-6 text-lg shadow-glow-primary transition-all hover:scale-105"
            >
              Comenzar el Test
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
          <p className="font-body text-xs text-primary-400 mt-4">
            Gratuito • Sin registro • Resultados inmediatos
          </p>
        </motion.div>
      </div>
    </div>
  );
}
